import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChildProfile {
  id: number;
  name: string;
  gender: string;
  dob: string;
  created_at?: string;
  updated_at?: string;
}

export interface ChildFullData {
  profile: ChildProfile;
  growth: any[];
  milestones: any[];
  vaccinations: any[];
  teeth: any[];
  sleep: any[];
  feeding: any[];
  diapers: any[];
  health_temp: any[];
  health_med: any[];
  memories: any[];
  activities: any[];
  moments: any[];
  growth_milestones: any[];
}

// ─── Service ──────────────────────────────────────────────────────────────────

const childDevelopmentService = {
  // ── Children CRUD ──────────────────────────────────────────────────────────

  getChildren: async (): Promise<ChildProfile[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.childDevelopment.children);
    return response.data.results || response.data;
  },

  createChild: async (data: { name: string; gender: string; dob: string }): Promise<ChildProfile> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.children, data);
    return response.data;
  },

  updateChild: async (childId: number, data: Partial<{ name: string; gender: string; dob: string }>): Promise<ChildProfile> => {
    const response = await axiosInstance.patch(API_ENDPOINTS.childDevelopment.childDetail(childId), data);
    return response.data;
  },

  // ── Full data (single API call to load everything) ─────────────────────────

  getFullData: async (childId: number): Promise<ChildFullData> => {
    const response = await axiosInstance.get(API_ENDPOINTS.childDevelopment.fullData(childId));
    return response.data;
  },

  // ── Bulk import (for localStorage migration) ──────────────────────────────

  bulkImport: async (childId: number, data: any): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.bulkImport(childId), data);
    return response.data;
  },

  // ── Growth ─────────────────────────────────────────────────────────────────

  saveGrowthEntry: async (childId: number, data: {
    date: string;
    height_cm?: number | '';
    weight_kg?: number | '';
    head_circ_cm?: number | '';
    notes?: string;
  }): Promise<any> => {
    const payload = {
      ...data,
      height_cm: data.height_cm !== '' ? data.height_cm : null,
      weight_kg: data.weight_kg !== '' ? data.weight_kg : null,
      head_circ_cm: data.head_circ_cm !== '' ? data.head_circ_cm : null,
    };
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.growth(childId), payload);
    return response.data;
  },

  updateGrowthEntry: async (childId: number, entryId: number, data: any): Promise<any> => {
    const payload = {
      ...data,
      height_cm: data.height_cm !== '' ? data.height_cm : null,
      weight_kg: data.weight_kg !== '' ? data.weight_kg : null,
      head_circ_cm: data.head_circ_cm !== '' ? data.head_circ_cm : null,
    };
    const response = await axiosInstance.patch(
      `${API_ENDPOINTS.childDevelopment.growth(childId)}${entryId}/`,
      payload
    );
    return response.data;
  },

  deleteGrowthEntry: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.growth(childId)}${entryId}/`);
  },

  // ── Milestones (upsert) ────────────────────────────────────────────────────

  saveMilestone: async (childId: number, data: {
    milestone_key: string | number;
    achieved?: boolean;
    achieved_date?: string | null;
    status?: string | null;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.milestones(childId), data);
    return response.data;
  },

  // ── Vaccinations (upsert) ──────────────────────────────────────────────────

  saveVaccination: async (childId: number, data: {
    vaccine_id: number;
    completed: boolean;
    completed_date?: string | null;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.vaccinations(childId), data);
    return response.data;
  },

  // ── Teeth (upsert) ────────────────────────────────────────────────────────

  saveTooth: async (childId: number, data: {
    tooth_id: number;
    appeared: boolean;
    appeared_date?: string | null;
    comment?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.teeth(childId), data);
    return response.data;
  },

  // ── Sleep ──────────────────────────────────────────────────────────────────

  saveSleepEntry: async (childId: number, data: {
    date: string;
    start_time: string;
    end_time: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.sleep(childId), data);
    return response.data;
  },

  deleteSleepEntry: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.sleep(childId)}${entryId}/`);
  },

  // ── Feeding ────────────────────────────────────────────────────────────────

  saveFeedingEntry: async (childId: number, data: {
    date: string;
    feeding_type: string;
    start_time: string;
    end_time?: string;
    side?: string;
    amount_ml?: number | '';
    bottle_type?: string;
    food_description?: string;
    notes?: string;
  }): Promise<any> => {
    const payload = {
      ...data,
      amount_ml: data.amount_ml !== '' ? data.amount_ml : null,
    };
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.feeding(childId), payload);
    return response.data;
  },

  deleteFeedingEntry: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.feeding(childId)}${entryId}/`);
  },

  // ── Diapers ────────────────────────────────────────────────────────────────

  saveDiaperEntry: async (childId: number, data: {
    date: string;
    time: string;
    diaper_type: string;
    color?: string;
    consistency?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.diapers(childId), data);
    return response.data;
  },

  deleteDiaperEntry: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.diapers(childId)}${entryId}/`);
  },

  // ── Health Temp ────────────────────────────────────────────────────────────

  saveHealthTemp: async (childId: number, data: {
    date: string;
    time: string;
    temperature: number | '';
    unit: string;
    notes?: string;
  }): Promise<any> => {
    const payload = {
      ...data,
      temperature: data.temperature !== '' ? data.temperature : null,
    };
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.healthTemp(childId), payload);
    return response.data;
  },

  deleteHealthTemp: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.healthTemp(childId)}${entryId}/`);
  },

  // ── Health Med ─────────────────────────────────────────────────────────────

  saveHealthMed: async (childId: number, data: {
    date: string;
    time: string;
    medicine_name: string;
    dosage: string;
    notes?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.healthMed(childId), data);
    return response.data;
  },

  deleteHealthMed: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.healthMed(childId)}${entryId}/`);
  },

  // ── Memories ───────────────────────────────────────────────────────────────

  saveMemory: async (childId: number, data: {
    title: string;
    date: string;
    description: string;
    photo?: File | null;
  }): Promise<any> => {
    // Use multipart when a photo is attached so the image uploads correctly
    if (data.photo) {
      const form = new FormData();
      form.append('title', data.title);
      form.append('date', data.date);
      form.append('description', data.description);
      form.append('photo', data.photo);
      const response = await axiosInstance.post(
        API_ENDPOINTS.childDevelopment.memories(childId),
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    }
    const { photo, ...jsonData } = data;
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.memories(childId), jsonData);
    return response.data;
  },

  deleteMemory: async (childId: number, entryId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.childDevelopment.memories(childId)}${entryId}/`);
  },

  // ── Activities (upsert) ────────────────────────────────────────────────────

  saveActivity: async (childId: number, data: {
    activity_key: string;
    completed: boolean;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.activities(childId), data);
    return response.data;
  },

  // ── Moments (upsert) ──────────────────────────────────────────────────────

  saveMoment: async (childId: number, data: {
    moment_key: string;
    completed: boolean;
    completed_date?: string | null;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.moments(childId), data);
    return response.data;
  },

  // ── Growth Milestones (upsert) ────────────────────────────────────────────

  saveGrowthMilestone: async (childId: number, data: {
    milestone_id: number;
    completed: boolean;
    completed_date?: string | null;
    value?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.childDevelopment.growthMilestones(childId), data);
    return response.data;
  },
};

export default childDevelopmentService;
